import type { CrremDisplayMetric } from "features/energy-analysis-dashboard/model/types";

import { Paper, Stack } from "@mui/material";
import { LoadingOrEmptyWrapper } from "components/LoadingAndEmptyHandler";
import { StrandingChartWithEchart } from "components/Modernization/StrandingChartWithEchart";
import { WizardContext } from "context/Wizard";
import { CRREM_DISPLAY_METRIC } from "features/energy-analysis-dashboard/model/constants";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "utils/styling";

import type { CrremPathwayViewSettings } from "../model/types";

import { useCrremChartSource } from "../hooks/useCrremChartSource";
import { useCrremPathwayChart } from "../hooks/useCrremPathwayChart";
import { buildModernizationOverlayLines } from "../lib/buildModernizationOverlayLines";
import { buildStrandingScatterSeries } from "../lib/buildStrandingScatterSeries";
import { collectChartYears, valueAtYear } from "../lib/collectPathwayYears";
import { CrremPathwayChartCanvas } from "./CrremPathwayChartCanvas";
import { CrremPathwayChartControls } from "./CrremPathwayChartControls";
import { CrremPathwayEmptyState } from "./CrremPathwayEmptyState";
import { CrremPathwayIncompleteNotice } from "./CrremPathwayIncompleteNotice";

type WizardCrremChartProps = {
  className?: string;
  onYearsChange?: (years: number[]) => void;
  projectId: string;
};

const yearsFromLegacyRows = (rows?: { year?: number }[]) =>
  (rows ?? []).map((row) => row.year).filter((year): year is number => typeof year === "number");

const yearsEqual = (left: number[], right: number[]) =>
  left.length === right.length && left.every((year, index) => year === right[index]);

const WizardPathwayChart = ({ className, onYearsChange, projectId }: WizardCrremChartProps) => {
  const { t } = useTranslation();
  const { data, quantity, section, setQuantity, yearRange } = useContext(WizardContext);
  const chart = useCrremPathwayChart(projectId, t("BuildingProfile.energyAnalysis.unit"));
  const { displayMetric, updateView } = chart;
  const modernizationLines = useMemo(
    () => buildModernizationOverlayLines(data, chart.areaDivisor, chart.grossArea),
    [chart.areaDivisor, chart.grossArea, data]
  );
  const overlayLines = useMemo(
    () => [...chart.overlayLines, ...modernizationLines],
    [chart.overlayLines, modernizationLines]
  );
  const allYears = useMemo(
    () =>
      collectChartYears(
        modernizationLines.map((line) => line.points),
        chart.years
      ),
    [chart.years, modernizationLines]
  );
  const years = useMemo(
    () => allYears.filter((year) => year >= yearRange[0] && year <= yearRange[1]),
    [allYears, yearRange]
  );
  const labels = useMemo(
    () => ({
      baseline: t("BuildingProfile.crremPathway.baseline"),
      pathway: t(
        displayMetric === CRREM_DISPLAY_METRIC.kwh
          ? "BuildingProfile.crremPathway.targetKwh"
          : "BuildingProfile.crremPathway.targetCo2"
      ),
      year: t("BuildingProfile.crremPathway.year"),
    }),
    [displayMetric, t]
  );
  const scatterSeries = useMemo(() => {
    const currentPoints = chart.overlayLines[0]?.points ?? chart.baseline;
    const modernizedPoints = modernizationLines.find((line) => line.name === "Modernized")?.points ?? [];
    const rows = allYears.map((year) => {
      const current = valueAtYear(currentPoints, year);
      const crrem = valueAtYear(chart.pathway, year);
      const modernized = valueAtYear(modernizedPoints, year);
      return {
        crrem: crrem ?? undefined,
        current: current ?? undefined,
        modernized: modernized ?? undefined,
        year,
      };
    });
    return buildStrandingScatterSeries(rows)
      .map((series) => ({
        ...series,
        points: series.points.filter((point) => point.x >= yearRange[0] && point.x <= yearRange[1]),
      }))
      .filter((series) => series.points.length > 0);
  }, [allYears, chart.baseline, chart.overlayLines, chart.pathway, modernizationLines, yearRange]);

  const lastReportedYearsRef = useRef<number[]>([]);
  useEffect(() => {
    if (!onYearsChange) return;
    if (yearsEqual(lastReportedYearsRef.current, allYears)) return;
    lastReportedYearsRef.current = allYears;
    onYearsChange(allYears);
  }, [allYears, onYearsChange]);

  const handleViewChange = useCallback(
    (change: Partial<CrremPathwayViewSettings>) => {
      updateView(change);
      if (change.displayMetric) {
        setQuantity(change.displayMetric === CRREM_DISPLAY_METRIC.kwh ? "consumption" : "emission");
      }
    },
    [setQuantity, updateView]
  );

  useEffect(() => {
    if (section !== "crrem") return;
    const targetMetric: CrremDisplayMetric = quantity === "consumption" ? "kwh" : "co2";
    if (displayMetric !== targetMetric) {
      updateView({ displayMetric: targetMetric });
    }
  }, [displayMetric, quantity, section, updateView]);

  return (
    <Paper className={cn("relative p-1", className)}>
      <Stack className="min-h-0 w-full flex-col items-stretch gap-4">
        <CrremPathwayChartControls
          correctionOptions={chart.correctionOptions}
          displayMetric={displayMetric}
          onSave={chart.saveSettings}
          onScenarioChange={chart.setScenarioKey}
          onViewChange={handleViewChange}
          partitionBy={chart.partitionBy}
          scale={chart.scale}
          scenarios={chart.scenarios}
          selectedScenarioKey={chart.selectedScenarioKey}
          settings={chart.settings}
          visibleCorrectionIds={chart.visibleCorrectionIds}
        />
        <CrremPathwayIncompleteNotice missing={chart.missing} />
        <LoadingOrEmptyWrapper height="52rem" showEmpty={chart.isEmpty} showLoading={chart.isLoading}>
          <CrremPathwayChartCanvas
            baseline={chart.baseline}
            heightClassName="h-[40rem]"
            labels={labels}
            overlayLines={overlayLines}
            partitionBy={chart.partitionBy}
            pathway={chart.pathway}
            scatterSeries={scatterSeries}
            stacks={chart.stacks}
            unit={chart.unit}
            xAxisType={chart.partitionBy === "none" ? "value" : "category"}
            years={years}
          />
        </LoadingOrEmptyWrapper>
      </Stack>
    </Paper>
  );
};

export const WizardCrremChart = ({ className, onYearsChange, projectId }: WizardCrremChartProps) => {
  const source = useCrremChartSource(projectId);
  const handleLegacyYearsChange = useCallback(
    (crremData: { year?: number }[]) => {
      onYearsChange?.(yearsFromLegacyRows(crremData));
    },
    [onYearsChange]
  );

  useEffect(() => {
    if (source === "empty") onYearsChange?.([]);
  }, [onYearsChange, source]);

  if (!source) {
    return <LoadingOrEmptyWrapper height="52rem" showLoading />;
  }
  if (source === "new") {
    return <WizardPathwayChart className={className} onYearsChange={onYearsChange} projectId={projectId} />;
  }
  if (source === "legacy") {
    return (
      <StrandingChartWithEchart
        className={className}
        onCrremChangeUseCallback={handleLegacyYearsChange}
        projectId={projectId}
        title=""
      />
    );
  }
  return <CrremPathwayEmptyState className={className} projectId={projectId} />;
};
